import React, { useState, useEffect, useMemo } from "react";
import DataTable from "react-data-table-component";

const DatatablePreviewPanel = ({ previewData, isSegment }) => {
  const rows = useMemo(() => previewData?.rows || [], [previewData]);

  const [filteredRows, setFilteredRows] = useState([]);
  const [filters, setFilters] = useState({});
  const [operators, setOperators] = useState({});
  const [columnTypes, setColumnTypes] = useState({});
  const [perPage, setPerPage] = useState(10);

  useEffect(() => {
    setFilteredRows(rows);

    if (rows.length > 0) {
      const firstRow = rows[0];
      const filterDefaults = {};
      const operatorDefaults = {};
      const types = {};

      Object.entries(firstRow).forEach(([key, value]) => {
        filterDefaults[key] = "";
        operatorDefaults[key] = "=";
        types[key] = typeof value === "number" ? "number" : "string";
      });

      setFilters(filterDefaults);
      setOperators(operatorDefaults);
      setColumnTypes(types);
    }
  }, [rows]);

  const handleFilterChange = (col, value) => {
    const updatedFilters = { ...filters, [col]: value };
    setFilters(updatedFilters);
    applyFilters(updatedFilters, operators);
  };

  const handleOperatorChange = (col, operator) => {
    const updatedOperators = { ...operators, [col]: operator };
    setOperators(updatedOperators);
    applyFilters(filters, updatedOperators);
  };

  const applyFilters = (filters, ops) => {
    const result = rows.filter((row) =>
      Object.entries(filters).every(([col, val]) => {
        const rowVal = row[col];
        const type = columnTypes[col];
        const op = ops[col];

        if (!val) return true;

        if (type === "number") {
          const numVal = parseFloat(val);
          if (isNaN(numVal)) return true;

          switch (op) {
            case "=": return rowVal === numVal;
            case ">": return rowVal > numVal;
            case "<": return rowVal < numVal;
            case ">=": return rowVal >= numVal;
            case "<=": return rowVal <= numVal;
            default: return true;
          }
        } else {
          return rowVal?.toString().toLowerCase().includes(val.toLowerCase());
        }
      })
    );

    setFilteredRows(result);
  };

  const columns = Object.keys(rows[0] || {}).map((col) => ({
    name: (
      <div
        className="d-flex flex-column align-items-start bg-white border p-2 rounded w-100"
        style={{ minHeight: "110px" }}
      >
        <div
          className={`fw-semibold text-dark mb-1 text-break ${
            filters[col] ? "text-primary" : ""
          }`}
        >
          {col}
        </div>
        {columnTypes[col] === "number" && (
          <select
            className="form-select form-select-sm mb-1 w-100"
            value={operators[col] || "="}
            onClick={(e) => e.stopPropagation()}
            onChange={(e) => handleOperatorChange(col, e.target.value)}
          >
            <option value="=">=</option>
            <option value=">">&gt;</option>
            <option value="<">&lt;</option>
            <option value=">=">&ge;</option>
            <option value="<=">&le;</option>
          </select>
        )}
        <input
          type={columnTypes[col] === "number" ? "number" : "text"}
          className="form-control form-control-sm w-100"
          placeholder="Filter..."
          value={filters[col] || ""}
          onClick={(e) => e.stopPropagation()}
          onChange={(e) => handleFilterChange(col, e.target.value)}
        />
      </div>
    ),
    selector: (row) => row[col],
    cell: (row) => {
      const val = row[col];
      // If value is an array of objects, render as internal table with responsive scroll
      if (Array.isArray(val) && val.length > 0 && typeof val[0] === "object") {
        return (
          <div className="table-responsive">
            <table
              className="table table-bordered table-sm mb-0"
              style={{ width: "100%", minWidth: "400px" }}
            >
              <thead>
                <tr>
                  {Object.keys(val[0]).map((key) => (
                    <th
                      key={key}
                      className="bg-light text-dark"
                      style={{ whiteSpace: "nowrap" }}
                    >
                      {key}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {val.map((item, idx) => (
                  <tr key={idx}>
                    {Object.keys(val[0]).map((key, i) => (
                      <td key={i}>{item[key]?.toString()}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        );
      }
      // If value is an array of primitives, join with |
      if (Array.isArray(val)) {
        return val.join(" | ");
      }
      // Boolean fix
      if (typeof val === "boolean") {
        return val.toString();
      }
      return val;
    },
    sortable: true,
  }));

  return (
    <div className="container-fluid px-0">
      <div className="d-flex justify-content-end mb-2">
        <button
          className="btn btn-outline-secondary btn-sm"
          onClick={() => {
            const cleared = Object.fromEntries(
              Object.keys(filters).map((k) => [k, ""])
            );
            setFilters(cleared);
            applyFilters(cleared, operators);
          }}
        >
          <i className="fa fa-rotate-left me-1"></i> Reset Filters
        </button>
      </div>

      <div className="table-responsive shadow-sm rounded border">
        <DataTable
          columns={columns}
          data={filteredRows}
          pagination
          paginationPerPage={perPage}
          paginationRowsPerPageOptions={[10, 25, 50, 100]}
          onChangeRowsPerPage={(newPerPage) => setPerPage(newPerPage)}
          persistTableHead
          striped
          highlightOnHover
          responsive
          customStyles={{
            table: {
              style: {
                borderCollapse: "collapse",
                width: "100%",
              },
            },
            headRow: {
              style: {
                position: "sticky",
                top: 0,
                zIndex: 11,
                backgroundColor: "#f8f9fa",
                borderBottom: "2px solid #dee2e6",
              },
            },
            headCells: {
              style: {
                border: "1px solid #dee2e6",
                backgroundColor: "#f8f9fa",
                textAlign: "center",
                verticalAlign: "top",
                padding: "8px",
                minWidth: "120px", // smaller for responsiveness
                whiteSpace: "normal", // allow wrapping
              },
            },
            rows: {
              style: {
                borderBottom: "1px solid #dee2e6",
              },
            },
            cells: {
              style: {
                border: "1px solid #dee2e6",
                textAlign: "center",
                verticalAlign: "middle",
                padding: "6px",
                minWidth: "120px", // smaller for responsiveness
                whiteSpace: "normal", // allow wrapping
              },
            },
          }}
        />
      </div>
    </div>
  );
};

export default DatatablePreviewPanel;
